/**
 * Settings service — wraps the self-service account + preferences endpoints.
 * All calls use the shared `api` instance (Bearer token auto-attached).
 */
import api from './api'

export interface UserPreferences {
  privateAccount: boolean
  activityStatus: boolean
  profileVisibility: string
  pushNotifications: boolean
  emailNotifications: boolean
  campaignUpdates: boolean
  reminderFrequency: string
  language: string
  fontSize: string
  compactMode: boolean
  updatedAt?: string | null
}

export interface ChangeEmailResult {
  accessToken: string
  refreshToken: string
  tokenType: string
  expiresIn: number
  user: unknown
}

const unwrap = <T>(r: any): T => r.data.data as T

export const settingsService = {
  getPreferences: (): Promise<UserPreferences> =>
    api.get('/users/me/preferences').then(unwrap<UserPreferences>),

  /** Partial update — only non-null fields are applied server-side. */
  updatePreferences: (partial: Partial<UserPreferences>): Promise<UserPreferences> =>
    api.put('/users/me/preferences', partial).then(unwrap<UserPreferences>),

  changePassword: (currentPassword: string, newPassword: string): Promise<void> =>
    api.put('/users/me/password', { currentPassword, newPassword }).then(() => undefined),

  /** Returns a fresh token pair (JWT is email-subjected, so the session must be replaced). */
  changeEmail: (newEmail: string): Promise<ChangeEmailResult> =>
    api.put('/users/me/email', { newEmail }).then(unwrap<ChangeEmailResult>),

  deactivateAccount: (): Promise<void> =>
    api.post('/users/me/deactivate', {}).then(() => undefined),

  deleteAccount: (password: string): Promise<void> =>
    api.delete('/users/me', { data: { password } }).then(() => undefined),

  downloadMyData: async (): Promise<void> => {
    const res = await api.get('/users/me/data', { responseType: 'blob' })
    const blob = new Blob([res.data], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  },
}

export default settingsService